import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { dataApi } from "@/lib/api/admin-api";
import { useLingui } from "@lingui/react/macro";
import {
  ArrowLeft,
  Building2,
  Globe,
  Mail,
  Phone,
  CalendarDays,
  MoreHorizontal,
  PlusCircle,
  SquarePen,
  Copy,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ActivityLogTable } from "@/components/activity/ActivityLogTable";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { InfoCard } from "@/components/common/InfoCard";
import { ImageWithFallback } from "@/components/common/ImageWithFallback";
import { format } from "date-fns";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { CreateLocationDialog } from "@/components/locations";
import { useIsTablet } from "@/hooks/use-mobile";
import { PageHeader } from "@/components/layout/PageHeaderSlot";
import { toast } from "sonner";
import { CompanyFormDialog } from "@/components/companies";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Route = createFileRoute(
  "/(protected)/_layout/companies_/$companyId",
)({
  component: CompanyDetailPage,
});

type Tab = "details" | "address" | "billing" | "audit";

function CompanyDetailPage() {
  const { t } = useLingui();
  const { companyId } = useParams({
    from: "/(protected)/_layout/companies_/$companyId",
  });
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const queryClient = useQueryClient();
  const isTablet = useIsTablet();
  const [activeTab, setActiveTab] = useState<Tab>("details");
  const [isEditOpen, setIsEditOpen] = useState(false);

  const companyIdNum = Number(companyId);
  const isValidId = Number.isFinite(companyIdNum) && companyIdNum > 0;

  const { data: company, isLoading } = useQuery({
    queryKey: ["companies", companyIdNum],
    queryFn: () => dataApi.getCompany(companyIdNum, ["address"]),
    enabled: isAuthenticated && isValidId,
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const toggleActiveMutation = useMutation({
    mutationFn: (isActive: boolean) => dataApi.updateCompany(companyIdNum, { isActive }),
    onSuccess: (_data, isActive) => {
      // The detail query is keyed ["companies", id], so the broad key covers it.
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      toast.success(isActive ? t`Concept activated` : t`Concept deactivated`);
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Failed to update concept`);
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5 p-5">
        <Skeleton className="h-6 w-40" />
        <div className="flex gap-6">
          <Skeleton className="size-40 rounded-2xl shrink-0" />
          <div className="flex flex-col gap-3 justify-center">
            <Skeleton className="h-10 w-72" />
            <Skeleton className="h-5 w-52" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
        <Skeleton className="h-10 w-full" />
        <div className="flex flex-col gap-5">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <Building2 className="h-12 w-12 text-(--color-text-secondary)/30" />
        <p className="text-(--color-text-secondary)">{t`Concept not found`}</p>
        <Button variant="outline" onClick={() => router.history.back()}>
          {t`Back to Concepts`}
        </Button>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "details", label: t`Concept Details` },
    { id: "address", label: t`Concept Address` },
    { id: "billing", label: t`Concept Billing Address` },
    { id: "audit", label: t`Audit Logs` },
  ];

  const actionH = isTablet ? "h-10" : "h-13";

  // ── Shared actions (reused by the desktop hero row and the tablet top bar) ──
  const createLocationAction = (
    <CreateLocationDialog
      defaultCompanyId={company.id}
      onCreated={() => {
        void queryClient.invalidateQueries({ queryKey: ["companies", company.id] });
        void queryClient.invalidateQueries({ queryKey: ["companies"] });
      }}
      trigger={(
        <Button
          variant="outline"
          className={cn(actionH, "rounded-[10px] px-4 border-(--shell-border) gap-2")}
        >
          <PlusCircle className="size-5" />
          <span className="text-base font-medium">{t`Create location`}</span>
        </Button>
      )}
    />
  );

  const moreAction = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(actionH, "rounded-[10px] px-4 border-(--shell-border) gap-2")}
        >
          <MoreHorizontal className="size-5" />
          <span className="text-base font-medium">{t`More`}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => copyToClipboard(company.slug)}>
          {t`Copy concept ID`}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={toggleActiveMutation.isPending}
          onSelect={() => toggleActiveMutation.mutate(!company.isActive)}
        >
          {company.isActive ? t`Deactivate concept` : t`Activate concept`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const editAction = (
    <Button
      onClick={() => setIsEditOpen(true)}
      className={cn(
        actionH,
        "rounded-[10px] px-4 bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) gap-2",
      )}
    >
      <SquarePen className="size-5" />
      <span className="text-base font-medium">{t`Edit details`}</span>
    </Button>
  );

  return (
    <div className="flex flex-col gap-5 p-3">

      {/* Tablet: compact top-bar header rendered into the app header slot */}
      {isTablet && (
        <PageHeader>
          <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
            <button
              type="button"
              onClick={() => router.history.back()}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) transition-colors hover:bg-(--color-surface-muted)"
              title={t`Back`}
            >
              <ArrowLeft className="size-5" />
            </button>
            <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary) truncate">
              {company.name}
            </h1>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.6875rem] font-bold shrink-0",
                company.isActive
                  ? "bg-(--color-success-bg) text-(--color-success-text)"
                  : "bg-(--color-surface-muted) text-(--color-text-secondary)",
              )}
            >
              {company.isActive ? t`Active` : t`Inactive`}
            </span>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {createLocationAction}
              {moreAction}
              {editAction}
            </div>
          </div>
        </PageHeader>
      )}

      {/* Page header */}
      {!isTablet && (
        <div className="flex flex-col gap-2">
          {/* Back link */}
          <button
            type="button"
            onClick={() => router.history.back()}
            className="flex items-center gap-2 px-3 py-2 w-fit rounded-lg hover:bg-(--color-surface-accent) transition-colors"
          >
            <ArrowLeft className="size-5 text-(--color-text-secondary)" />
            <span className="text-base font-medium text-(--color-text-secondary)">{t`Back to Concepts`}</span>
          </button>

          {/* Hero */}
          <div className="flex items-start justify-between px-3">
            <div className="flex items-center gap-6">
              {/* Logo */}
              <div className="size-40 shrink-0 rounded-2xl border-4 border-white shadow-lg overflow-hidden bg-(--color-surface-muted) flex items-center justify-center">
                <ImageWithFallback
                  src={company.logoUrl}
                  alt={company.name}
                  className="size-full object-cover"
                  fallback={<Building2 className="size-16 text-(--color-text-secondary)/30" />}
                />
              </div>

              {/* Name + meta */}
              <div className="flex flex-col gap-3 justify-center">
                <div className="flex items-center gap-4">
                  <h1 className="text-5xl font-medium text-(--color-text-primary) leading-none">
                    {company.name}
                  </h1>
                  <Badge
                    className={cn(
                      "rounded-full px-3 py-1 text-sm font-medium border-transparent shrink-0",
                      company.isActive
                        ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90"
                        : "bg-(--color-surface-chip-inactive) text-(--color-text-primary)",
                    )}
                  >
                    {company.isActive ? t`Active` : t`Inactive`}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base text-(--color-text-secondary)">
                    {t`Concept ID:`}{" "}
                    <span className="font-medium text-(--color-text-primary)">{company.slug}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(company.slug)}
                    className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
                  >
                    <Copy className="size-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {createLocationAction}
              {moreAction}
              {editAction}
            </div>
          </div>
        </div>
      )}

      {/* Info cards */}
      <div className="flex gap-2 bg-(--color-surface-muted) rounded-xl p-2">
        <InfoCard
          icon={<Globe className="size-5 text-(--color-text-secondary)" />}
          label={t`Concept ID`}
          value={company.slug}
        />
        <InfoCard
          icon={<Mail className="size-5 text-(--color-text-secondary)" />}
          label={t`Email`}
          value={company.email ?? "—"}
        />
        <InfoCard
          icon={<Phone className="size-5 text-(--color-text-secondary)" />}
          label={t`Phone`}
          value={company.phone ?? "—"}
        />
        <InfoCard
          icon={<CalendarDays className="size-5 text-(--color-text-secondary)" />}
          label={t`Last updated`}
          value={format(new Date(company.updatedAt), "dd/MM/yyyy, HH:mm:ss")}
        />
      </div>

      {/* Divider */}
      <div className="border-t border-(--color-border)" />

      {/* Tab bar */}
      <div className="flex gap-1 bg-(--color-surface-muted) rounded-md p-1 w-full">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap",
              activeTab === tab.id
                ? "bg-(--color-surface-content) font-medium text-(--color-text-primary) shadow-xs"
                : "font-normal text-(--color-text-secondary) hover:text-(--color-text-secondary)",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "details" && (
        <CompanyDetailsTab
          company={company}
          onCopy={copyToClipboard}
          onEdit={() => setIsEditOpen(true)}
        />
      )}
      {activeTab === "address" && (
        <CompanyAddressTab company={company} />
      )}
      {activeTab === "audit" && (
        <ActivityLogTable scope={{ entityType: "company", entityId: company.id }} showActor />
      )}
      {activeTab !== "details" && activeTab !== "address" && activeTab !== "audit" && (
        <div className="py-16 text-center text-sm text-(--color-text-secondary)">
          {t`Coming soon`}
        </div>
      )}

      <CompanyFormDialog
        company={company}
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
      />
    </div>
  );
}

function FormRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="flex gap-8 items-start">
        <span className="w-85 shrink-0 text-sm font-medium text-(--color-text-primary) leading-5">
          {label}
        </span>
        <div className="flex-1">{children}</div>
      </div>
      <div className="h-px bg-(--shell-border)" />
    </>
  );
}

function CompanyDetailsTab({
  company,
  onCopy,
  onEdit,
}: {
  company: NonNullable<Awaited<ReturnType<typeof dataApi.getCompany>>>;
  onCopy: (text: string) => void;
  onEdit: () => void;
}) {
  const { t } = useLingui();

  return (
    <div className="px-4">
      {/* Section header */}
      <div className="flex flex-col gap-5 pb-px">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-medium text-(--color-text-primary) leading-7">
              {t`Concept Details`}
            </h2>
            <p className="text-sm text-(--color-text-secondary) leading-5">
              {t`View and manage concept information.`}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={onEdit}
            className="h-10 rounded-[10px] px-4 border-(--shell-border) gap-2 shrink-0"
          >
            <SquarePen className="size-4" />
            <span className="text-sm font-medium text-(--color-text-primary)">{t`Edit`}</span>
          </Button>
        </div>
        <div className="h-px bg-(--shell-border)" />
      </div>

      {/* Form rows */}
      <div className="flex flex-col gap-5 mt-5">
        <FormRow label={t`Concept name`}>
          <span className="text-base font-medium text-(--color-text-primary)">{company.name}</span>
        </FormRow>

        <FormRow label={t`Concept ID`}>
          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-(--color-text-primary)">{company.slug}</span>
            <button
              type="button"
              onClick={() => onCopy(company.slug)}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
            >
              <Copy className="size-4" />
            </button>
          </div>
        </FormRow>

        <FormRow label={t`Status`}>
          <Badge
            className={cn(
              "rounded-full px-3 py-1 text-sm font-medium border-transparent",
              company.isActive
                ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90"
                : "bg-(--color-surface-chip-inactive) text-(--color-text-primary)",
            )}
          >
            {company.isActive ? t`Active` : t`Inactive`}
          </Badge>
        </FormRow>

        <FormRow label={t`Email`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {company.email ?? "—"}
          </span>
        </FormRow>

        <FormRow label={t`Phone`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {company.phone ?? "—"}
          </span>
        </FormRow>

        {company.description && (
          <FormRow label={t`Description`}>
            <span className="text-base font-medium text-(--color-text-primary)">
              {company.description}
            </span>
          </FormRow>
        )}

        <FormRow label={t`Logo image`}>
          <div className="w-75 h-65 rounded-2xl border-4 border-white shadow-lg overflow-hidden bg-(--color-surface-muted) flex items-center justify-center">
            <ImageWithFallback
              src={company.logoUrl}
              alt={company.name}
              className="size-full object-cover"
              fallback={<Building2 className="size-16 text-(--color-text-secondary)/30" />}
            />
          </div>
        </FormRow>
      </div>
    </div>
  );
}

function CompanyAddressTab({
  company,
}: {
  company: NonNullable<Awaited<ReturnType<typeof dataApi.getCompany>>>;
}) {
  const { t } = useLingui();

  const location = company.locations?.[0];

  const fullAddress = [
    location?.addressLine1,
    location?.city,
    location?.state,
    location?.postalCode,
    location?.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="px-4">
      {/* Section header */}
      <div className="flex flex-col gap-5 pb-px">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-medium text-(--color-text-primary) leading-7">
              {t`Concept Address`}
            </h2>
            <p className="text-sm text-(--color-text-secondary) leading-5">
              {t`This is the concept's headquarters location. Edit it from that location's page.`}
            </p>
          </div>
        </div>
        <div className="h-px bg-(--shell-border)" />
      </div>

      {/* Form rows */}
      <div className="flex flex-col gap-5 mt-5">
        <FormRow label={t`Address`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {fullAddress || "—"}
          </span>
        </FormRow>

        <FormRow label={t`Address 1`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {location?.addressLine1 ?? "—"}
          </span>
        </FormRow>

        <FormRow label={t`City`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {location?.city ?? "—"}
          </span>
        </FormRow>

        <FormRow label={t`State / Province`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {location?.state ?? "—"}
          </span>
        </FormRow>

        <FormRow label={t`ZIP Code`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {location?.postalCode ?? "—"}
          </span>
        </FormRow>

        <FormRow label={t`Phone`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {location?.phone ?? company.phone ?? "—"}
          </span>
        </FormRow>

        <FormRow label={t`Country`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {location?.country ?? "—"}
          </span>
        </FormRow>
      </div>
    </div>
  );
}
